/**
 * SenaraiWarisTab Component
 * Tab for selecting and configuring heirs (waris) in the Faraid calculator
 * 
 * Reference: FaraidInfo.md Section 3 (Waris-Waris)
 */

import { useCallback } from 'react';
import { Card } from '@components/common/Card';
import { Button } from '@components/common/Button';
import type { DeceasedInfo, HeirInput, HeirType } from '../../types';
import { HEIR_DEFINITIONS, HEIRS_BY_CATEGORY, CATEGORY_LABELS } from '../../types/heirDefinitions';
import styles from './SenaraiWarisTab.module.css';

interface SenaraiWarisTabProps {
  deceased: DeceasedInfo;
  heirs: Record<HeirType, HeirInput>;
  onDeceasedChange: (deceased: DeceasedInfo) => void;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}

/**
 * Heir row component for individual heir input
 */
const HeirRow = ({
  heirType,
  input,
  deceased,
  isDisabled,
  disabledReason,
  onChange,
}: {
  heirType: HeirType;
  input: HeirInput;
  deceased: DeceasedInfo;
  isDisabled: boolean;
  disabledReason?: string;
  onChange: (type: HeirType, input: Partial<HeirInput>) => void;
}) => {
  const definition = HEIR_DEFINITIONS[heirType];
  const canHaveMultiple = definition.canHaveMultiple;
  const maxCount = definition.maxCount;
  
  // Primary and substitute heirs that should always be editable
  // Even if blocked, user should be able to input their count
  const alwaysEditableHeirs: HeirType[] = [
    'bapa',
    'ibu',
    'datuk',
    'nenek_sebelah_bapa',
    'nenek_sebelah_ibu',
  ];
  const isAlwaysEditable = alwaysEditableHeirs.includes(heirType);
  
  const handleCountChange = useCallback((delta: number) => {
    const newCount = Math.max(0, Math.min(maxCount, input.count + delta));
    onChange(heirType, { 
      count: newCount,
      isPresent: newCount > 0,
    });
  }, [heirType, input.count, maxCount, onChange]);
  
  const handleCountInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      const newCount = Math.max(0, Math.min(maxCount, value));
      onChange(heirType, { 
        count: newCount,
        isPresent: newCount > 0,
      });
    }
  }, [heirType, maxCount, onChange]);
  
  // Check if this heir is valid for the deceased's gender
  const isGenderValid = (() => {
    if (heirType === 'suami' && deceased.gender === 'lelaki') return false;
    if (heirType === 'isteri' && deceased.gender === 'perempuan') return false;
    return true;
  })();
  
  // For always-editable heirs, don't disable based on blocking
  // Blocking only affects calculation, not input
  const effectivelyDisabled = (!isAlwaysEditable && isDisabled) || !isGenderValid;
  const reasonText = !isGenderValid 
    ? `Tidak berkenaan untuk simati ${deceased.gender}`
    : (isAlwaysEditable && disabledReason ? `${disabledReason} (masih boleh diinput)` : disabledReason);
  
  // Don't apply disabled styling for always-editable heirs
  const shouldShowDisabled = effectivelyDisabled && !isAlwaysEditable;
  
  return (
    <div className={`${styles.heirRow} ${shouldShowDisabled ? styles.disabled : ''}`}>
      <div className={styles.heirLabel}>
        <span className={styles.heirName}>{definition.labelMs}</span>
        {reasonText && (
          <span className={styles.disabledReason}>{reasonText}</span>
        )}
      </div>
      <div className={styles.heirControls}>
        {/* Show counter buttons for multiple-count heirs OR always-editable single-count heirs */}
        {canHaveMultiple || isAlwaysEditable ? (
          <div className={styles.counterGroup}>
            <input
              type="number"
              className={styles.countInput}
              value={input.count}
              onChange={handleCountInput}
              min={0}
              max={maxCount}
              disabled={effectivelyDisabled}
            />
            <Button
              variant="primary"
              size="sm"
              className={styles.counterButton}
              onClick={() => handleCountChange(-1)}
              disabled={effectivelyDisabled || input.count <= 0}
              aria-label={`Kurangkan ${definition.labelMs}`}
            >
              -
            </Button>
            <Button
              variant="primary"
              size="sm"
              className={styles.counterButton}
              onClick={() => handleCountChange(1)}
              disabled={effectivelyDisabled || input.count >= maxCount}
              aria-label={`Tambah ${definition.labelMs}`}
            >
              +
            </Button>
          </div>
        ) : (
          <div className={styles.toggleGroup}>
            <span className={`${styles.countBadge} ${input.count > 0 ? styles.active : ''}`}>
              {input.count}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Category section component
 */
const CategorySection = ({
  category,
  heirTypes,
  heirs,
  deceased,
  onHeirChange,
}: {
  category: string;
  heirTypes: HeirType[];
  heirs: Record<HeirType, HeirInput>;
  deceased: DeceasedInfo;
  onHeirChange: (type: HeirType, input: Partial<HeirInput>) => void;
}) => {
  const categoryLabel = CATEGORY_LABELS[category];
  
  // Check which heirs are blocked
  const getBlockedInfo = (heirType: HeirType): { isBlocked: boolean; reason?: string } => {
    const definition = HEIR_DEFINITIONS[heirType];
    
    for (const blockerType of definition.blockedBy) {
      const blockerInput = heirs[blockerType];
      if (blockerInput?.isPresent && blockerInput?.count > 0) {
        const blockerDef = HEIR_DEFINITIONS[blockerType];
        return {
          isBlocked: true,
          reason: `Terdinding oleh ${blockerDef.labelMs}`,
        };
      }
    }
    
    return { isBlocked: false };
  };
  
  return (
    <div className={styles.categorySection}>
      <div className={styles.categoryHeader}>
        {categoryLabel.ms}
      </div>
      <div className={styles.categoryContent}>
        {heirTypes.map((heirType) => {
          const input = heirs[heirType] || { type: heirType, count: 0, isPresent: false };
          const blockedInfo = getBlockedInfo(heirType);
          
          return (
            <HeirRow
              key={heirType}
              heirType={heirType}
              input={input}
              deceased={deceased}
              isDisabled={blockedInfo.isBlocked}
              disabledReason={blockedInfo.reason}
              onChange={onHeirChange}
            />
          );
        })}
      </div>
    </div>
  );
};

/**
 * SenaraiWarisTab main component
 */
export const SenaraiWarisTab = ({
  deceased,
  heirs,
  onDeceasedChange,
  onHeirChange,
}: SenaraiWarisTabProps) => {
  const handleGenderChange = useCallback((gender: 'lelaki' | 'perempuan') => {
    onDeceasedChange({ gender });
    
    // Reset spouse selection when gender changes
    if (gender === 'lelaki') {
      // Reset husband if exists
      onHeirChange('suami', { count: 0, isPresent: false });
    } else {
      // Reset wife if exists
      onHeirChange('isteri', { count: 0, isPresent: false });
    }
  }, [onDeceasedChange, onHeirChange]);
  
  return (
    <div className={styles.container}>
      {/* Left Panel - Simati & Waris Utama & Ganti */}
      <div className={styles.leftPanel}>
        <Card className={styles.card}>
          <div className={styles.sectionTitle}>Senarai Waris</div>
          
          {/* Simati (Deceased) Section */}
          <div className={styles.categorySection}>
            <div className={styles.categoryHeader}>Simati</div>
            <div className={styles.categoryContent}>
              <div className={styles.heirRow}>
                <div className={styles.heirLabel}>
                  <span className={styles.heirName}>Jantina</span>
                </div>
                <div className={styles.heirControls}>
                  <div className={styles.genderToggle}>
                    <button
                      type="button"
                      className={`${styles.genderButton} ${deceased.gender === 'lelaki' ? styles.active : ''}`}
                      onClick={() => handleGenderChange('lelaki')}
                    >
                      Lelaki
                    </button>
                    <button
                      type="button"
                      className={`${styles.genderButton} ${deceased.gender === 'perempuan' ? styles.active : ''}`}
                      onClick={() => handleGenderChange('perempuan')}
                    >
                      Perempuan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Waris Utama */}
          <CategorySection
            category="waris_utama"
            heirTypes={HEIRS_BY_CATEGORY.waris_utama}
            heirs={heirs}
            deceased={deceased}
            onHeirChange={onHeirChange}
          />
          
          {/* Waris Ganti */}
          <CategorySection
            category="waris_ganti"
            heirTypes={HEIRS_BY_CATEGORY.waris_ganti}
            heirs={heirs}
            deceased={deceased}
            onHeirChange={onHeirChange}
          />
        </Card>
      </div>
      
      {/* Right Panel - Waris Kedua */}
      <div className={styles.rightPanel}>
        <Card className={styles.card}>
          <div className={styles.sectionTitle}>Waris Kedua</div>
          
          <CategorySection
            category="waris_kedua"
            heirTypes={HEIRS_BY_CATEGORY.waris_kedua}
            heirs={heirs}
            deceased={deceased}
            onHeirChange={onHeirChange}
          />
        </Card>
      </div>
    </div>
  );
};
